import { Check, X } from 'lucide-react';
import { z } from 'zod';

import { Form } from '@/modules/forms/components/Form';
import { FormSelect } from '@/modules/forms/components/FormSelect';
import { FormTextarea } from '@/modules/forms/components/FormTextarea';
import { Button } from '@/modules/ui/components/button/Button';
import type { components } from '@/types/apiSchema';

export type JobNoteType = components['schemas']['JobNoteType'];
export type CompanyNoteType = components['schemas']['CompanyNoteType'];
export type CandidateJobNoteType =
  components['schemas']['CandidateJobNoteType'];

const createNoteSchema = (types: readonly string[]) =>
  z.object({
    content: z.string().min(1, 'Note content is required'),
    type: z.enum(types as [string, ...string[]]),
  });

export type NoteFormData = z.infer<ReturnType<typeof createNoteSchema>>;

export interface NoteTypeOption {
  value: string;
  label: string;
}

interface NoteFormProps {
  onSubmit: (data: NoteFormData) => void;
  defaultValues?: NoteFormData;
  isLoading?: boolean;
  submitLabel?: string;
  onCancel?: () => void;
  rows?: number;
  typeOptions: readonly NoteTypeOption[];
  /** If provided, hides the type selector and uses this as the default type */
  defaultType?: string;
}

export const NoteForm = ({
  onSubmit,
  defaultValues,
  isLoading = false,
  submitLabel = 'Add Note',
  onCancel,
  typeOptions,
  defaultType,
}: NoteFormProps) => {
  const noteSchema = createNoteSchema(typeOptions.map((o) => o.value));
  const resolvedType = defaultType ?? typeOptions[0]?.value ?? 'General';
  const showTypeSelector = !defaultType;

  return (
    <Form
      schema={noteSchema}
      onSubmit={onSubmit}
      mode="onSubmit"
      defaultValues={defaultValues ?? { content: '', type: resolvedType }}
    >
      <div className="space-y-3">
        {showTypeSelector && (
          <FormSelect
            name="type"
            label="Type"
            options={[...typeOptions]}
            placeholder="Select note type"
          />
        )}
        <FormTextarea name="content" placeholder="Add a note..." rows={6} />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isLoading}
            >
              <X className="mr-2 size-4" />
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            size={onCancel ? 'sm' : undefined}
            loading={isLoading}
          >
            {onCancel && <Check className="mr-2 size-4" />}
            {submitLabel}
          </Button>
        </div>
      </div>
    </Form>
  );
};
